import { useState } from 'react';
import Navbar2 from '../component/navbar2';
import Footer from '../components/BrandFooter';
import Reveal from '../components/landing/Reveal';
import { BRAND } from '@/brand';
import { API_BASE_URL } from '@/utils/env';
import { SETUP_TRACKS, type SetupTrack } from '@/components/SetupTrackPicker';
import '../styles/publicPages.css';
import { L7_MEDIA } from '@/data/landingMedia';

// Accent — gold on corporate, blue on the consulting brand.
const GOLD = BRAND === 'corporate' ? '#FFD54F' : '#1976D2';
const GOLD_D = BRAND === 'corporate' ? '#E6B400' : '#1D4ED8';
const GOLD_SOFT = BRAND === 'corporate' ? 'rgba(255,213,79,.18)' : 'rgba(25,118,210,.14)';

const contactImg = L7_MEDIA.dubaiSkyline;
const cu1 = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/cu1.png';
const cu2 = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/cu2.png';
const cu3 = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/cu3.png';
const cu4 = 'https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/cu4.png';

const INFO_CARDS = [
  {
    icon: cu1,
    bg: '#E8F3FD',
    title: 'Visit Us',
    lines: ['Dubai Creek Harbour', 'South Tower 2, Office 1303', 'Dubai, United Arab Emirates'],
  },
  {
    icon: cu2,
    bg: '#FFF8E1',
    title: 'Get in Touch',
    lines: ['support@conncct.com', '+971 50 164 1651', 'WhatsApp available'],
  },
  {
    icon: cu4,
    bg: '#E6F4EC',
    title: 'Business Hours',
    lines: ['Sunday – Friday', '9:00 AM – 7:00 PM (GST)', 'Saturday: Closed'],
  },
];

type Status = 'idle' | 'sending' | 'sent' | 'error';

export default function ContactUs() {
  const [name, setName]       = useState('');
  const [email, setEmail]     = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [track, setTrack]     = useState<SetupTrack | ''>('');
  const [status, setStatus]   = useState<Status>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !message) return;
    setStatus('sending');
    // Persist to the shared leads store so it reaches the admin Leads screen
    // (previously this only opened a mailto and nothing was recorded).
    try {
      const res = await fetch(`${API_BASE_URL}/demo/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: name.trim().split(' ')[0] || name.trim(),
          lastName: name.trim().split(' ').slice(1).join(' ') || '-',
          email: email.trim(),
          companyName: 'Contact form',
          helpWith: 'Contact enquiry',
          setupTrack: track || null,
          discussionTopic: [subject && `Subject: ${subject}`, message].filter(Boolean).join(' · '),
        }),
      });
      if (!res.ok) throw new Error('send failed');
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="pub-page">
      {/* Hero lifts in on load; the cards, form and partner tiles reveal on scroll. */}
      <Reveal selector=".pub-page .reveal" />

      <div style={{ position: 'sticky', top: 0, zIndex: 50 }}>
        <Navbar2 bgColor="white" logoTo="/" />
      </div>

      {/* ── HERO ── */}
      <section className="pub-hero cream" style={{ textAlign: 'center' }}>
        <div className="pub-inner narrow rise">
          <span className="pub-eye" style={{ marginBottom: 22, display: 'inline-flex' }}>Contact</span>
          <h1 className="pub-h1" style={{ color: '#17263A', marginBottom: 20 }}>
            We'd love to <span className="pub-hl">hear from you</span>
          </h1>
          <p className="pub-lead" style={{ margin: '0 auto 0', textAlign: 'center', maxWidth: 560 }}>
            Whether you have a question, want to explore how Conncct can help your business, or just want to say hello, our team is here.
          </p>
        </div>

        <div className="pub-inner" style={{ marginTop: 56 }}>
          <div className="pub-img reveal" style={{ maxHeight: 480 }}>
            <img
              src={contactImg}
              alt="Conncct contact"
              style={{ aspectRatio: '21/9', objectFit: 'cover' }}
            />
          </div>
        </div>
      </section>

      {/* ── INFO CARDS ── */}
      <section className="pub-sec white">
        <div className="pub-inner">
          <div className="pub-grid-3">
            {INFO_CARDS.map(({ icon, bg, title, lines }) => (
              <div
                key={title}
                className="reveal"
                style={{
                  background: bg,
                  borderRadius: 24,
                  padding: '36px 32px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                }}
              >
                <div style={{ width: 56, height: 56, borderRadius: 16, background: '#fff', display: 'grid', placeItems: 'center', boxShadow: '0 2px 12px rgba(23,38,58,.08)' }}>
                  <img src={icon} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} />
                </div>
                <strong style={{ fontSize: 17, fontWeight: 800, color: '#17263A' }}>{title}</strong>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {lines.map(l => (
                    <span key={l} style={{ fontSize: 14.5, color: '#42505F', lineHeight: 1.6 }}>{l}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FORM ── */}
      <section className="pub-sec tinted">
        <div className="pub-inner narrow">
          <div className="reveal" style={{ textAlign: 'center', marginBottom: 44 }}>
            <span className="pub-eye" style={{ marginBottom: 18, display: 'inline-flex' }}>Send a message</span>
            <h2 className="pub-h2" style={{ color: '#17263A' }}>Tell us how we can help</h2>
            <p className="pub-lead" style={{ margin: '12px auto 0', textAlign: 'center' }}>
              Our team responds within 24–48 business hours.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="reveal"
            style={{
              background: '#fff',
              borderRadius: 24,
              padding: '44px 48px',
              display: 'flex',
              flexDirection: 'column',
              gap: 20,
              boxShadow: '0 12px 40px -16px rgba(23,38,58,.12)',
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#17263A' }}>Full name *</label>
                <input
                  type="text"
                  required
                  placeholder="Sara Al Maktoum"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  style={{
                    height: 48, border: '1.5px solid #E8E2D2', borderRadius: 12, padding: '0 16px',
                    fontSize: 15, color: '#17263A', fontFamily: 'Montserrat, system-ui, sans-serif',
                    outline: 'none', background: '#FFFDF7', transition: 'border-color .15s',
                  }}
                  onFocus={e => (e.currentTarget.style.borderColor = GOLD_D)}
                  onBlur={e => (e.currentTarget.style.borderColor = '#E8E2D2')}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#17263A' }}>Email address *</label>
                <input
                  type="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  style={{
                    height: 48, border: '1.5px solid #E8E2D2', borderRadius: 12, padding: '0 16px',
                    fontSize: 15, color: '#17263A', fontFamily: 'Montserrat, system-ui, sans-serif',
                    outline: 'none', background: '#FFFDF7', transition: 'border-color .15s',
                  }}
                  onFocus={e => (e.currentTarget.style.borderColor = GOLD_D)}
                  onBlur={e => (e.currentTarget.style.borderColor = '#E8E2D2')}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#17263A' }}>Subject</label>
              <input
                type="text"
                placeholder="How can we help?"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                style={{
                  height: 48, border: '1.5px solid #E8E2D2', borderRadius: 12, padding: '0 16px',
                  fontSize: 15, color: '#17263A', fontFamily: 'Montserrat, system-ui, sans-serif',
                  outline: 'none', background: '#FFFDF7', transition: 'border-color .15s',
                }}
                onFocus={e => (e.currentTarget.style.borderColor = GOLD_D)}
                onBlur={e => (e.currentTarget.style.borderColor = '#E8E2D2')}
              />
            </div>

            {/* Which side of the funnel — optional here, because this form also
                takes partner, press and general enquiries. Tracked all the same,
                since admin Leads has no other way to tell before signup. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#17263A' }}>
                Where are you with your company?{' '}
                <span style={{ fontWeight: 500, color: '#9AA3AF' }}>(optional)</span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {SETUP_TRACKS.map(o => {
                  const on = track === o.id;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setTrack(on ? '' : o.id)}
                      aria-pressed={on}
                      style={{
                        textAlign: 'left', cursor: 'pointer', borderRadius: 12, padding: '12px 16px',
                        border: `1.5px solid ${on ? GOLD_D : '#E8E2D2'}`,
                        background: on ? GOLD_SOFT : '#FFFDF7',
                        fontFamily: 'Montserrat, system-ui, sans-serif', transition: 'border-color .15s, background .15s',
                      }}
                    >
                      <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: '#17263A' }}>{o.title}</span>
                      <span style={{ display: 'block', fontSize: 12, color: '#6E7888', marginTop: 2 }}>{o.hint}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: '#17263A' }}>Message *</label>
              <textarea
                required
                rows={5}
                placeholder="Tell us about your business and what you're looking for..."
                value={message}
                onChange={e => setMessage(e.target.value)}
                style={{
                  border: '1.5px solid #E8E2D2', borderRadius: 12, padding: '14px 16px',
                  fontSize: 15, color: '#17263A', fontFamily: 'Montserrat, system-ui, sans-serif',
                  outline: 'none', background: '#FFFDF7', resize: 'vertical', transition: 'border-color .15s',
                }}
                onFocus={e => (e.currentTarget.style.borderColor = GOLD_D)}
                onBlur={e => (e.currentTarget.style.borderColor = '#E8E2D2')}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#6E7888' }}>
                Or email us directly at{' '}
                <a href="mailto:support@conncct.com" style={{ color: '#17263A', fontWeight: 700 }}>support@conncct.com</a>
              </p>
              <button
                type="submit"
                className="pub-btn primary"
                disabled={status === 'sending' || status === 'sent'}
              >
                {status === 'sent' ? 'Message sent ✓' : status === 'sending' ? 'Sending…' : 'Send message →'}
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* ── PARTNERSHIP CARD ── */}
      <section className="pub-sec tinted" style={{ paddingTop: 32, paddingBottom: 72 }}>
        <div className="pub-inner narrow">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div className="reveal" style={{ background: '#17263A', borderRadius: 24, padding: '36px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: GOLD_SOFT, display: 'grid', placeItems: 'center' }}>
                <img src={cu3} alt="" style={{ width: 26, height: 26, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
              </div>
              <strong style={{ fontSize: 17, fontWeight: 800, color: '#fff' }}>Partnerships &amp; Press</strong>
              <p style={{ margin: 0, fontSize: 14, color: 'rgba(206,219,242,.8)', lineHeight: 1.7 }}>
                For partnership opportunities, investment inquiries, or media requests, reach out to our team directly.
              </p>
              <a href="mailto:info@conncct.com" style={{ color: GOLD, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
                info@conncct.com →
              </a>
            </div>

            <div className="reveal" style={{ background: '#F5F1E8', borderRadius: 24, padding: '36px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: '#fff', display: 'grid', placeItems: 'center', boxShadow: '0 2px 8px rgba(23,38,58,.08)' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#E6B400" strokeWidth="2" strokeLinecap="round" width={24} height={24}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <strong style={{ fontSize: 17, fontWeight: 800, color: '#17263A' }}>Partner Program</strong>
              <p style={{ margin: 0, fontSize: 14, color: '#42505F', lineHeight: 1.7 }}>
                Refer clients to Conncct and earn up to 50% commission. Learn more about our partner program.
              </p>
              <a href="/partner" style={{ color: '#17263A', fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
                Learn about partnering →
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
